import React, { useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  ShippingRate,
  CarrierConfig,
  CreateRateRequest,
  UpdateRateRequest,
} from "../../model/SiteShipping";
import { X } from "lucide-react";

interface Props {
  zoneId: string;
  rate: ShippingRate | null;
  carriers: CarrierConfig[];
  onClose: (saved: boolean) => void;
}

const ShippingRateForm: React.FC<Props> = ({
  zoneId,
  rate,
  carriers,
  onClose,
}) => {
  const isEdit = !!rate;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [carrierConfigId, setCarrierConfigId] = useState(
    rate?.carrierConfigId || "",
  );
  const [serviceLevel, setServiceLevel] = useState(rate?.serviceLevel || "");
  const [serviceName, setServiceName] = useState(rate?.serviceName || "");
  const [minWeightGrams, setMinWeightGrams] = useState(
    rate?.minWeightGrams ?? 0,
  );
  const [maxWeightGrams, setMaxWeightGrams] = useState(
    rate?.maxWeightGrams ?? 0,
  );
  const [priceEuros, setPriceEuros] = useState(
    rate ? (rate.priceAmount / 100).toFixed(2) : "",
  );
  const [currency, setCurrency] = useState(rate?.currency || "EUR");
  const [transitDaysMin, setTransitDaysMin] = useState(
    rate?.transitDaysMin ?? 1,
  );
  const [transitDaysMax, setTransitDaysMax] = useState(
    rate?.transitDaysMax ?? 5,
  );
  const [isActive, setIsActive] = useState(rate?.isActive ?? true);
  const [freeShippingEuros, setFreeShippingEuros] = useState(
    rate ? (rate.freeShippingThreshold / 100).toFixed(2) : "0",
  );

  const eurosToCents = (val: string) =>
    Math.round(parseFloat(val || "0") * 100);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        const body: UpdateRateRequest = {
          serviceLevel,
          serviceName,
          minWeightGrams,
          maxWeightGrams,
          priceAmount: eurosToCents(priceEuros),
          currency,
          transitDaysMin,
          transitDaysMax,
          isActive,
          freeShippingThreshold: eurosToCents(freeShippingEuros),
        };
        await api.put(
          `${API_URLS.SHIPPING_ZONES}/${zoneId}/rates/${rate!.id}`,
          body,
        );
      } else {
        const body: CreateRateRequest = {
          carrierConfigId,
          serviceLevel,
          serviceName,
          minWeightGrams,
          maxWeightGrams,
          priceAmount: eurosToCents(priceEuros),
          currency,
          transitDaysMin,
          transitDaysMax,
          isActive,
          freeShippingThreshold: eurosToCents(freeShippingEuros),
        };
        await api.post(`${API_URLS.SHIPPING_ZONES}/${zoneId}/rates`, body);
      }
      onClose(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar tarifa.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isEdit ? "Editar Tarifa" : "Nova Tarifa"}
          </h3>
          <button onClick={() => onClose(false)}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transportadora
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={carrierConfigId}
                onChange={(e) => setCarrierConfigId(e.target.value)}
              >
                <option value="">Selecionar...</option>
                {carriers
                  .filter((c) => c.isActive)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName} ({c.carrierSlug})
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nivel de Servico
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={serviceLevel}
                onChange={(e) => setServiceLevel(e.target.value)}
                placeholder="standard, express..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Servico
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                placeholder="Envio Standard"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Peso Min. (gramas)
              </label>
              <input
                type="number"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={minWeightGrams}
                onChange={(e) =>
                  setMinWeightGrams(parseInt(e.target.value) || 0)
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Peso Max. (gramas, 0=sem limite)
              </label>
              <input
                type="number"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={maxWeightGrams}
                onChange={(e) =>
                  setMaxWeightGrams(parseInt(e.target.value) || 0)
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preco (EUR)
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={priceEuros}
                onChange={(e) => setPriceEuros(e.target.value)}
                placeholder="4.99"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Moeda
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dias Transito Min
              </label>
              <input
                type="number"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={transitDaysMin}
                onChange={(e) =>
                  setTransitDaysMin(parseInt(e.target.value) || 1)
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dias Transito Max
              </label>
              <input
                type="number"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={transitDaysMax}
                onChange={(e) =>
                  setTransitDaysMax(parseInt(e.target.value) || 5)
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gratis a partir de (EUR, 0=desativado)
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={freeShippingEuros}
                onChange={(e) => setFreeShippingEuros(e.target.value)}
                placeholder="50.00"
              />
            </div>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4"
              />
              Ativa
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            className="button is-light is-small"
            onClick={() => onClose(false)}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="button is-primary is-small"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShippingRateForm;
